
import React from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Instagram } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { translations } from '@/utils/translations';
import { useIsMobile } from '@/hooks/use-mobile';

const Footer = () => {
  const { currentLanguage, getLocalizedPath } = useLanguage();
  const t = translations[currentLanguage];
  const isMobile = useIsMobile();

  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-secondary text-white pt-16 pb-8 relative">
      <div className="container mx-auto px-4 relative z-10">
        <div className="flex flex-col lg:flex-row">
          {/* Text content - Left side */}
          <div className="lg:w-3/5 lg:pr-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div>
                <h3 className="text-xl font-bold mb-4 text-white">Future Level</h3>
                <p className="text-gray-300 mb-4">
                  {t.footerDescription}
                </p>
                <div className="flex space-x-4">
                  <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-white transition-colors">
                    <Facebook size={20} />
                  </a>
                  <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-white transition-colors">
                    <Instagram size={20} />
                  </a>
                </div>
              </div>

              <div>
                <h3 className="text-xl font-bold mb-4 text-white">{t.services}</h3>
                <ul className="space-y-2">
                  <li>
                    <Link to={getLocalizedPath("services/product-videos")} className="text-gray-300 hover:text-white transition-colors">
                      {t.productVideos}
                    </Link>
                  </li>
                  <li>
                    <Link to={getLocalizedPath("services/social-media-ads")} className="text-gray-300 hover:text-white transition-colors">
                      {t.socialMediaAds}
                    </Link>
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-bold mb-4 text-white">{t.contact}</h3>
                <address className="not-italic text-gray-300">
                  <p className="mb-1">Future Level</p>
                  <p className="mb-1">
                    <a href="mailto:info@future-level.ch" className="hover:text-white transition-colors">
                      info@future-level.ch
                    </a>
                  </p>
                  <p>
                    <a href="tel:+41441234567" className="hover:text-white transition-colors">
                      +41 44 123 45 67
                    </a>
                  </p>
                </address>
              </div>
            </div>

            <hr className="border-gray-700 my-8" />

            <div className="flex flex-col md:flex-row justify-between items-center">
              <p className="text-gray-400 text-sm mb-4 md:mb-0">
                &copy; {currentYear} Future Level. {t.allRightsReserved}
              </p>
              <div className="flex space-x-4">
                <Link to={getLocalizedPath("legal")} className="text-gray-400 text-sm hover:text-white transition-colors">
                  {t.impressum}
                </Link>
                <Link to={getLocalizedPath("privacy")} className="text-gray-400 text-sm hover:text-white transition-colors">
                  {t.privacy}
                </Link>
                <Link to={getLocalizedPath("terms")} className="text-gray-400 text-sm hover:text-white transition-colors">
                  {t.terms}
                </Link>
              </div>
            </div>
          </div>
          
          <div className={`lg:w-2/5 ${isMobile ? 'mt-8' : ''} relative h-[300px] lg:h-auto rounded-lg overflow-hidden`}>
            <div className="absolute inset-0 w-full h-full overflow-hidden">
              <iframe 
                allow="fullscreen;autoplay" 
                allowFullScreen 
                src="https://streamable.com/e/4r6zz5?autoplay=1&muted=1&nocontrols=1&loop=1" 
                width="100%" 
                height="100%" 
                style={{
                  border: "none", 
                  width: "100%", 
                  height: "100%", 
                  position: "absolute", 
                  left: "0px", 
                  top: "0px", 
                  overflow: "hidden",
                  pointerEvents: "none",
                  objectFit: "cover",
                  filter: "brightness(0.6) contrast(1.2)"
                }}
                title="Footer Background Video"
              ></iframe>
            </div>
            <div className="absolute inset-0 bg-black/30 backdrop-blur-[1px]"></div>
            
            <div className="absolute bottom-4 left-4 right-4 text-white text-center">
              <h4 className="text-lg font-future">Future Level</h4>
              <p className="text-sm text-gray-300">AI-Powered Video Marketing</p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
